/**
 * CCO › Relatórios › Regulatório (NR 9/ANA): indicadores operacionais Nível I com a memória de cálculo (US-3707) — Nexora Grid.
 *
 * Criado em: 26/10/2026 · Última modificação: 26/10/2026 · Versão: 1.0.0
 *
 * Indicadores da Norma de Referência ANA nº 9/2024 (Resolução ANA nº 211/2024) por município e período (ano = apuração
 * oficial; mês = parcial): valor, unidade, padrão de excelência e situação; "não calculável" diz o que falta. Cada número
 * mostra de onde veio (informado pela operadora, telemetria ou SNIS/SINISA). PDF e CSV para a agência. A engenharia
 * (`pode_informar`) informa os dados primários com a fonte — vale o mais recente, o histórico fica.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, baixar, ErroApi } from "../api";
import { useCco } from "./contexto";

type Memoria = { variavel: string; valor: number; unidade: string; origem: string };
type Indicador = { codigo: string; nome: string; unidade?: string; formula: string; excelencia?: number; sentido?: string; valor: number | null;
  situacao: "atende" | "nao_atende" | "nao_calculavel" | "insatisfatorio_sem_condicao"; faltam?: string[]; memoria: Memoria[];
  condicao?: { codigo: string; valor: number; minimo: number } };
type Relatorio = { fonte: string; ano: number; mes: number; aviso: string | null; indicadores: Indicador[]; pode_informar: boolean;
  variaveis: Record<string, { unidade: string; opcional: boolean }> };

const SITUACAO: Record<Indicador["situacao"], [string, string]> = {
  atende: ["Atende", "situacao-validado"], nao_atende: ["Não atende", "situacao-rejeitado"],
  nao_calculavel: ["Não calculável", "situacao-pendente"], insatisfatorio_sem_condicao: ["Insatisfatório (sem condição de avaliação)", "situacao-rejeitado"] };

export function RelatorioNR9() {
  const { empresa } = useCco();
  const [municipios, setMunicipios] = useState<{ codigo: string; nome: string }[]>([]);
  const [municipio, setMunicipio] = useState("");
  const [ano, setAno] = useState(new Date().getFullYear() - 1);
  const [mes, setMes] = useState(0);
  const [rel, setRel] = useState<Relatorio | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [dado, setDado] = useState({ variavel: "volume_consumido", valor: "", fonte: "" });

  useEffect(() => {
    if (!empresa) return;
    api<{ municipios: { codigo: string; nome: string; cadastrado: boolean }[] }>(`/v1/municipios?empresa=${empresa}`)
      .then((r) => { const m = r.municipios.filter((x) => x.cadastrado); setMunicipios(m); setMunicipio((atual) => atual || m[0]?.codigo || ""); })
      .catch(() => setMunicipios([]));
  }, [empresa]);
  const consulta = `empresa=${empresa}&municipio=${municipio}&ano=${ano}${mes ? `&mes=${mes}` : ""}`;
  const carregar = useCallback(() => {
    if (!empresa || !municipio) return;
    api<Relatorio>(`/v1/regulatorio/nr9?${consulta}`).then((r) => { setRel(r); setAviso(null); })
      .catch((x) => setAviso(x instanceof ErroApi ? x.message : "Falha de rede."));
  }, [empresa, municipio, consulta]);
  useEffect(carregar, [carregar]);

  const informar = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api("/v1/regulatorio/nr9/dados", { metodo: "PUT", corpo: { empresa, municipio, ano, mes,
        valores: { [dado.variavel]: Number(dado.valor.replace(",", ".")) }, fonte: dado.fonte } });
      setAviso(`${dado.variavel} = ${dado.valor} gravado (fica no histórico).`);
      setDado({ ...dado, valor: "" });
      carregar();
    } catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
  };

  return (
    <section className="nx-cartao relatorio-nr9" aria-label="Regulatório NR 9">
      <h3>Regulatório (NR 9/ANA)</h3>
      <div className="linha-relatorio">
        <label>Município <select value={municipio} onChange={(e) => setMunicipio(e.target.value)}>
          {municipios.map((m) => <option key={m.codigo} value={m.codigo}>{m.nome}</option>)}</select></label>
        <label>Ano <input type="number" min={2000} max={2100} value={ano} onChange={(e) => setAno(Number(e.target.value))} /></label>
        <label>Mês <select value={mes} onChange={(e) => setMes(Number(e.target.value))}>
          <option value={0}>ano inteiro (oficial)</option>
          {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, "0")} (parcial)</option>)}</select></label>
        {rel && <>
          <button type="button" className="nx-botao" onClick={() => void baixar(`/v1/regulatorio/nr9.pdf?${consulta}`, `nr9_${municipio}_${ano}.pdf`)}>Baixar PDF</button>
          <button type="button" className="nx-botao" onClick={() => void baixar(`/v1/regulatorio/nr9.csv?${consulta}`, `nr9_${municipio}_${ano}.csv`)}>Baixar CSV</button>
        </>}
      </div>
      {aviso && <p className="nx-suave" role="status">{aviso}</p>}
      {rel?.aviso && <p className="nx-suave">{rel.aviso}</p>}
      {rel && (
        <div className="tabela-rolagem">
          <table className="tabela-composicao" aria-label="Indicadores NR 9">
            <thead><tr><th>Indicador</th><th>Valor</th><th>Excelência</th><th>Situação</th><th>De onde veio</th></tr></thead>
            <tbody>
              {rel.indicadores.map((i) => (
                <tr key={i.codigo}>
                  <td><b>{i.codigo}</b> {i.nome}</td>
                  <td className="nx-valor">{i.valor === null ? "—" : `${i.valor.toLocaleString("pt-BR")} ${i.unidade ?? ""}`}</td>
                  <td>{i.excelencia === undefined ? "—" : `${i.sentido?.startsWith("menor") ? "≤" : "≥"} ${i.excelencia.toLocaleString("pt-BR")}`}</td>
                  <td><span className={`nx-selo ${SITUACAO[i.situacao][1]}`}>{SITUACAO[i.situacao][0]}</span>
                    {i.condicao && <><br /><small className="nx-suave">{i.condicao.codigo}: {i.condicao.valor.toLocaleString("pt-BR")}% (mín. 95%)</small></>}</td>
                  <td>
                    {i.faltam?.length ? <small>Falta: {i.faltam.join(", ")}</small> : null}
                    {i.memoria.length > 0 && <details><summary>memória de cálculo</summary>
                      <small>{i.formula}</small>
                      <ul>{i.memoria.map((m) => <li key={m.variavel}><small>{m.variavel} = {m.valor.toLocaleString("pt-BR")} {m.unidade} — {m.origem}</small></li>)}</ul>
                    </details>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rel && <p className="nx-suave">{rel.fonte}.</p>}
      {rel?.pode_informar && (
        <form className="form-filial" aria-label="Informar dado da NR 9" onSubmit={(e) => void informar(e)}>
          <div className="nx-campo"><label htmlFor="nr9-var">Dado primário</label>
            <select id="nr9-var" value={dado.variavel} onChange={(e) => setDado({ ...dado, variavel: e.target.value })}>
              {Object.entries(rel.variaveis).map(([k, v]) => <option key={k} value={k}>{k} ({v.unidade})</option>)}</select></div>
          <div className="nx-campo"><label htmlFor="nr9-valor">Valor</label>
            <input id="nr9-valor" required inputMode="decimal" value={dado.valor} onChange={(e) => setDado({ ...dado, valor: e.target.value })} /></div>
          <div className="nx-campo"><label htmlFor="nr9-fonte">Fonte</label>
            <input id="nr9-fonte" required minLength={3} maxLength={200} value={dado.fonte} placeholder="ex.: sistema comercial, laudo do laboratório"
              onChange={(e) => setDado({ ...dado, fonte: e.target.value })} /></div>
          <button type="submit" className="nx-botao">Gravar</button>
        </form>
      )}
    </section>
  );
}
